/**
 * Project Store - Zustand State Management
 *
 * This store manages all project-related state:
 * - Beat nodes (the story beats)
 * - Project metadata (name, fps, etc.)
 * - Selected node
 * - Project file path
 *
 * ZUSTAND BASICS:
 * ================
 * Zustand is a lightweight state management library. Unlike Redux, it:
 * - Has minimal boilerplate
 * - Doesn't require providers or context
 * - Uses hooks directly for accessing state
 *
 * Usage in components:
 *   const nodes = useProjectStore((state) => state.nodes);
 *   const addNode = useProjectStore((state) => state.addNode);
 *
 * The `(state) => state.xxx` pattern is a selector that subscribes only
 * to changes in that specific piece of state (performance optimization).
 *
 * Learn more: https://docs.pmnd.rs/zustand/getting-started/introduction
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { v4 as uuidv4 } from 'uuid';
import { BeatNode, BeatNodeData, Project, DEFAULT_PROJECT, DEFAULT_BEAT_DATA, AspectRatio, CommentBoxNode, CommentBoxData, DEFAULT_COMMENT_BOX_DATA, CameraMovementType, CameraOverlay, ReferenceNode, ReferenceNodeData, DEFAULT_REFERENCE_DATA, NoteNode, NoteNodeData, DEFAULT_NOTE_DATA, TextOverlay, TitleNode, TitleNodeData, DEFAULT_TITLE_DATA, AudioTrack, DEFAULT_AUDIO_TRACK } from '@/types';
import { useHistoryStore, type NodeSnapshot, type StateSnapshot } from './historyStore';
import { NODE_MAX_COUNT, NODE_WARNING_COUNT } from '@/constants/app';
import { useNotificationStore } from './notificationStore';
import { useViewStore } from './viewStore';
import { usePlaybackStore } from './playbackStore';
import { BeatColorScheme, applyBeatColorScheme } from '@/styles/themes';
import { imageStore } from '@/utils/browserAPI';
import { blobManager } from '@/utils/blobManager';
import { extractWaveform, arrayBufferToBase64 } from '@/utils/audioWaveform';

// Reference count for legacy imageStore paths (images/...)
const imageRefCounts = new Map<string, number>();

function addImageRef(imagePath: string | null): void {
  if (!imagePath) return;
  const count = imageRefCounts.get(imagePath) || 1;
  imageRefCounts.set(imagePath, count + 1);
}

function releaseImageRef(imagePath: string | null): boolean {
  if (!imagePath) return false;
  const count = imageRefCounts.get(imagePath) || 1;
  if (count <= 1) {
    imageRefCounts.delete(imagePath);
    return true;
  }
  imageRefCounts.set(imagePath, count - 1);
  return false;
}

// =============================================================================
// STORE INTERFACE
// =============================================================================

/**
 * Defines the shape of our project store
 *
 * Contains both:
 * - State: The data (nodes, projectPath, etc.)
 * - Actions: Functions that modify the state
 */
interface ProjectStore {
  // ---------------------------------------------------------------------------
  // STATE
  // ---------------------------------------------------------------------------

  /** All beat nodes in the current project */
  nodes: BeatNode[];

  /** All title nodes in the project */
  titleNodes: TitleNode[];

  /** Comment boxes for grouping and annotating nodes */
  commentBoxes: CommentBoxNode[];

  /** Currently selected node IDs (empty array if none selected) */
  selectedNodeIds: string[];

  /** Currently selected title node IDs */
  selectedTitleIds: string[];

  /** All audio tracks in the project */
  audioTracks: AudioTrack[];

  /** Currently selected audio track IDs */
  selectedAudioIds: string[];

  /** Path to the current project file (null if unsaved) */
  projectPath: string | null;

  /** Project metadata */
  projectName: string;
  fps: number;
  aspectRatio: AspectRatio;
  beatColorScheme: BeatColorScheme;
  projectAuthor: string;
  projectDescription: string;

  /** Whether there are unsaved changes */
  isDirty: boolean;

  /** Clipboard for copy/paste operations (stores copied node data) */
  clipboard: BeatNode[];

  /** Whether to show the tutorial on new project creation */
  showTutorial: boolean;

  // ---------------------------------------------------------------------------
  // TUTORIAL ACTIONS
  // ---------------------------------------------------------------------------

  /** Set whether to show the tutorial */
  setShowTutorial: (show: boolean) => void;

  /** Close tutorial without persisting to localStorage */
  closeTutorial: () => void;

  /** Set tutorial preference in localStorage without closing */
  setTutorialPreference: (dismiss: boolean) => void;

  /** Mark the project as dirty (has unsaved changes) */
  markDirty: () => void;

  /** Last clicked position in the node view (for paste target) */
  lastClickedPosition: { x: number; y: number } | null;

  /** History of scene headings for autocomplete suggestions */
  sceneHeadingHistory: string[];

  /** All reference nodes in the reference library */
  referenceNodes: ReferenceNode[];

  /** Currently selected reference node IDs */
  selectedReferenceIds: string[];

  /** History of tags for autocomplete suggestions */
  tagHistory: string[];

  /** All note nodes in the reference library */
  noteNodes: NoteNode[];

  /** Currently selected note node IDs */
  selectedNoteIds: string[];

  // ---------------------------------------------------------------------------
  // NODE ACTIONS
  // ---------------------------------------------------------------------------

  /** Add a new beat node at the specified position */
  addNode: (position: { x: number; y: number }, data?: Partial<BeatNodeData>) => BeatNode;

  /** Update an existing node's data */
  updateNode: (nodeId: string, data: Partial<BeatNodeData>) => void;

  /** Add a scene heading to history */
  addSceneHeadingToHistory: (heading: string) => void;

  /** Clean up scene heading history */
  cleanupSceneHeadingHistory: () => void;

  /** Update a node's position */
  updateNodePosition: (nodeId: string, position: { x: number; y: number }) => void;

  /** Delete a node by ID */
  deleteNode: (nodeId: string) => void;

  /** Duplicate a node at a new position */
  duplicateNode: (nodeId: string, position: { x: number; y: number }) => BeatNode | null;

  /** Set which node is currently selected */
  selectNode: (nodeId: string | null) => void;

  /** Set multiple nodes as selected */
  selectNodes: (nodeIds: string[]) => void;

  /** Toggle a node's selection state */
  toggleNodeSelection: (nodeId: string) => void;

  /** Clear all node selections */
  clearSelection: () => void;

  /** Copy selected nodes to clipboard */
  copySelectedNodes: () => void;

  /** Paste nodes from clipboard */
  pasteNodes: () => void;

  /** Set the last clicked position */
  setLastClickedPosition: (position: { x: number; y: number }) => void;

  /** Update color for multiple selected nodes */
  updateSelectedNodesColor: (color: string) => void;

  /** Duplicate selected nodes in place */
  duplicateSelectedNodesInPlace: () => string[];

  /** Move selected nodes to shelf */
  moveSelectedNodesToShelf: () => void;

  /** Reorder a node in the timeline */
  reorderNodeInTimeline: (nodeId: string, targetIndex: number) => void;

  // ---------------------------------------------------------------------------
  // OVERLAY ACTIONS
  // ---------------------------------------------------------------------------

  /** Add a camera overlay to a beat node */
  addOverlay: (nodeId: string, overlayType: CameraMovementType) => void;

  /** Remove an overlay from a beat node */
  removeOverlay: (nodeId: string, overlayId: string) => void;

  /** Update overlay properties */
  updateOverlay: (nodeId: string, overlayId: string, updates: Partial<CameraOverlay>) => void;

  /** Add a text overlay to a beat node */
  addTextOverlay: (nodeId: string, text: string) => void;

  /** Remove a text overlay from a beat node */
  removeTextOverlay: (nodeId: string, textOverlayId: string) => void;

  /** Update text overlay properties */
  updateTextOverlay: (nodeId: string, textOverlayId: string, updates: Partial<TextOverlay>) => void;

  // ---------------------------------------------------------------------------
  // COMMENT BOX ACTIONS
  // ---------------------------------------------------------------------------

  /** Create a comment box around the selected nodes */
  createCommentBox: () => CommentBoxNode | null;

  /** Update a comment box */
  updateCommentBox: (boxId: string, updates: Partial<Pick<CommentBoxNode, 'position' | 'width' | 'height'> & { data: Partial<CommentBoxData> }>) => void;

  /** Delete a comment box */
  deleteCommentBox: (boxId: string) => void;

  // ---------------------------------------------------------------------------
  // REFERENCE NODE ACTIONS
  // ---------------------------------------------------------------------------

  /** Add a new reference node */
  addReferenceNode: (position: { x: number; y: number }, data?: Partial<ReferenceNodeData>) => ReferenceNode;

  /** Update an existing reference node */
  updateReferenceNode: (nodeId: string, data: Partial<ReferenceNodeData>) => void;

  /** Update a reference node's position */
  updateReferencePosition: (nodeId: string, position: { x: number; y: number }) => void;

  /** Delete a reference node */
  deleteReferenceNode: (nodeId: string) => void;

  /** Duplicate a reference node */
  duplicateReferenceNode: (nodeId: string, position: { x: number; y: number }) => ReferenceNode | null;

  /** Set which reference node is selected */
  selectReferenceNode: (nodeId: string | null) => void;

  /** Set multiple reference nodes as selected */
  selectReferenceNodes: (nodeIds: string[]) => void;

  /** Clear all reference node selections */
  clearReferenceSelection: () => void;

  /** Add a tag to history */
  addTagToHistory: (tag: string) => void;

  /** Send a reference as a beat node */
  sendReferenceAsBeatToEnd: (referenceId: string) => void;
  sendReferenceAsBeatAtPosition: (referenceId: string) => void;

  // ---------------------------------------------------------------------------
  // NOTE NODE ACTIONS
  // ---------------------------------------------------------------------------

  /** Add a new note node */
  addNoteNode: (position: { x: number; y: number }, data?: Partial<NoteNodeData>) => NoteNode;

  /** Update an existing note node */
  updateNoteNode: (nodeId: string, data: Partial<NoteNodeData>) => void;

  /** Update a note node's position */
  updateNotePosition: (nodeId: string, position: { x: number; y: number }) => void;

  /** Update a note node's size */
  updateNoteSize: (nodeId: string, width: number, height: number) => void;

  /** Delete a note node */
  deleteNoteNode: (nodeId: string) => void;

  /** Duplicate a note node */
  duplicateNoteNode: (nodeId: string, position: { x: number; y: number }) => NoteNode | null;

  /** Set which note node is selected */
  selectNoteNode: (nodeId: string | null) => void;

  /** Set multiple note nodes as selected */
  selectNoteNodes: (nodeIds: string[]) => void;

  /** Clear all note node selections */
  clearNoteSelection: () => void;

  // ---------------------------------------------------------------------------
  // TITLE NODE ACTIONS
  // ---------------------------------------------------------------------------

  /** Add a new title node at the specified timeline position */
  addTitleNode: (startTime: number, duration: number, data?: Partial<TitleNodeData>) => TitleNode;

  /** Update an existing title node's data */
  updateTitleNode: (nodeId: string, data: Partial<TitleNodeData>) => void;

  /** Delete a title node by ID */
  deleteTitleNode: (nodeId: string) => void;

  /** Update title node's position in timeline */
  updateTitleNodePosition: (nodeId: string, startTime: number) => void;

  /** Update title node's duration */
  updateTitleNodeDuration: (nodeId: string, duration: number) => void;

  /** Set which title node is selected */
  selectTitleNode: (nodeId: string | null) => void;

  /** Set multiple title nodes as selected */
  selectTitleNodes: (nodeIds: string[]) => void;

  /** Clear all title node selections */
  clearTitleSelection: () => void;

  /** Duplicate a title node */
  duplicateTitleNode: (nodeId: string, startTime?: number) => TitleNode | null;

  // ---------------------------------------------------------------------------
  // AUDIO TRACK ACTIONS
  // ---------------------------------------------------------------------------

  /** Add a new audio track */
  addAudioTrack: (audioData: string, fileName: string, fileType: string, duration: number, startTime?: number) => AudioTrack;

  /** Update an audio track's properties */
  updateAudioTrack: (trackId: string, updates: Partial<AudioTrack>) => void;

  /** Delete an audio track */
  deleteAudioTrack: (trackId: string) => void;

  /** Update audio track's position in timeline */
  updateAudioTrackPosition: (trackId: string, startTime: number) => void;

  /** Set which audio track is selected */
  selectAudioTrack: (trackId: string | null) => void;

  /** Set multiple audio tracks as selected */
  selectAudioTracks: (trackIds: string[]) => void;

  /** Clear all audio track selections */
  clearAudioSelection: () => void;

  // ---------------------------------------------------------------------------
  // PROJECT ACTIONS
  // ---------------------------------------------------------------------------

  /** Create a new empty project */
  newProject: (name?: string) => void;

  /** Load a project from parsed JSON data */
  loadProject: (project: Project, filePath: string) => void;

  /** Get the current project as a serializable object */
  getProjectData: () => Project;

  /** Set the project file path */
  setProjectPath: (path: string) => void;

  /** Update project metadata */
  setProjectName: (name: string) => void;
  setFps: (fps: number) => void;
  setAspectRatio: (aspectRatio: AspectRatio) => void;
  setBeatColorScheme: (scheme: BeatColorScheme) => void;
  setProjectAuthor: (author: string) => void;
  setProjectDescription: (description: string) => void;

  /** Mark the project as saved */
  markSaved: () => void;

  // ---------------------------------------------------------------------------
  // UTILITY ACTIONS
  // ---------------------------------------------------------------------------

  /** Get nodes sorted by their x-position */
  getSortedNodes: () => BeatNode[];

  /** Get the total duration of all beats */
  getTotalDuration: () => number;

  // ---------------------------------------------------------------------------
  // HISTORY ACTIONS
  // ---------------------------------------------------------------------------

  /** Save current state to history */
  saveHistory: () => void;

  /** Undo the last change */
  undo: () => void;

  /** Redo the last undone change */
  redo: () => void;

  /** Restore state from a snapshot */
  restoreSnapshot: (snapshot: StateSnapshot) => void;
}

// =============================================================================
// STORE IMPLEMENTATION
// =============================================================================

/**
 * Create the Zustand store with Immer middleware
 */
export const useProjectStore = create<ProjectStore>()(
  immer((set, get) => ({
  // ---------------------------------------------------------------------------
  // INITIAL STATE
  // ---------------------------------------------------------------------------

  nodes: [],
  commentBoxes: [],
  selectedNodeIds: [],
  projectPath: null,
  projectName: DEFAULT_PROJECT.name,
  fps: DEFAULT_PROJECT.fps,
  aspectRatio: DEFAULT_PROJECT.aspectRatio,
  beatColorScheme: 'orange-grey',
  projectAuthor: DEFAULT_PROJECT.author || '',
  projectDescription: DEFAULT_PROJECT.description || '',
  isDirty: false,
  clipboard: [],
  showTutorial: typeof window !== 'undefined' ? localStorage.getItem('emberboard-showTutorial') !== 'false' : true,
  lastClickedPosition: null,
  sceneHeadingHistory: [],
  referenceNodes: [],
  selectedReferenceIds: [],
  tagHistory: [],
  noteNodes: [],
  selectedNoteIds: [],
  titleNodes: [],
  selectedTitleIds: [],
  audioTracks: [],
  selectedAudioIds: [],

  // ---------------------------------------------------------------------------
  // NODE ACTIONS
  // ---------------------------------------------------------------------------

  addNode: (position, data = {}) => {
    const state = get();

    let title = data.title || DEFAULT_BEAT_DATA.title;
    const existingTitles = state.nodes.map(n => n.data.title);
    if (existingTitles.includes(title)) {
      let counter = 2;
      const baseName = title;
      while (existingTitles.includes(`${baseName} ${counter}`)) {
        counter++;
      }
      title = `${baseName} ${counter}`;
    }

    const newNode: BeatNode = {
      id: uuidv4(),
      type: 'beat',
      position,
      data: {
        ...DEFAULT_BEAT_DATA,
        ...data,
        title,
      },
    };

    set((state) => ({
      nodes: [...state.nodes, newNode],
      isDirty: true,
    }));

    return newNode;
  },

  updateNode: (nodeId, data) => {
    set((draft) => {
      const node = draft.nodes.find(n => n.id === nodeId);
      if (node) {
        Object.assign(node.data, data);
      }
      draft.isDirty = true;
    });
  },

  addSceneHeadingToHistory: (heading) => {
    set((draft) => {
      const trimmedHeading = heading.trim();
      if (!trimmedHeading) return;

      if (!draft.sceneHeadingHistory.includes(trimmedHeading)) {
        draft.sceneHeadingHistory.unshift(trimmedHeading);
        if (draft.sceneHeadingHistory.length > 50) {
          draft.sceneHeadingHistory.pop();
        }
      } else {
        const index = draft.sceneHeadingHistory.indexOf(trimmedHeading);
        draft.sceneHeadingHistory.splice(index, 1);
        draft.sceneHeadingHistory.unshift(trimmedHeading);
      }
      draft.isDirty = true;
    });
  },

  cleanupSceneHeadingHistory: () => {
    set((draft) => {
      const usedHeadings = new Set(
        draft.nodes
          .map(node => node.data.sceneHeading?.trim())
          .filter((heading): heading is string => !!heading)
      );

      const originalLength = draft.sceneHeadingHistory.length;
      draft.sceneHeadingHistory = draft.sceneHeadingHistory.filter(
        heading => usedHeadings.has(heading)
      );

      if (draft.sceneHeadingHistory.length !== originalLength) {
        draft.isDirty = true;
      }
    });
  },

  updateNodePosition: (nodeId, position) => {
    set((draft) => {
      const node = draft.nodes.find(n => n.id === nodeId);
      if (node) {
        node.position = position;
      }
      draft.isDirty = true;
    });
  },

  deleteNode: (nodeId) => {
    const state = get();
    const node = state.nodes.find((n) => n.id === nodeId);

    if (node?.data.imagePath) {
      const imagePath = node.data.imagePath;

      if (imagePath.startsWith('node-image:')) {
        const otherNodesWithSameImage = state.nodes.filter(
          n => n.id !== nodeId && n.data.imagePath === imagePath
        ).length;

        if (otherNodesWithSameImage === 0) {
          useHistoryStore.getState().addPendingImageCleanup(imagePath);
        } else {
          blobManager.release(imagePath);
        }
      } else {
        const shouldRevoke = releaseImageRef(imagePath);
        if (shouldRevoke) {
          useHistoryStore.getState().addPendingImageCleanup(imagePath);
        }
      }
    }

    set((state) => ({
      nodes: state.nodes.filter((node) => node.id !== nodeId),
      selectedNodeIds: state.selectedNodeIds.filter((id) => id !== nodeId),
      isDirty: true,
    }));
  },

  duplicateNode: (nodeId, position) => {
    const state = get();
    const originalNode = state.nodes.find((n) => n.id === nodeId);

    if (!originalNode) return null;

    if (state.nodes.length >= NODE_MAX_COUNT) {
      alert(`Maximum ${NODE_MAX_COUNT} beats reached. Cannot duplicate.`);
      return null;
    }

    if (state.nodes.length >= NODE_WARNING_COUNT) {
      useNotificationStore.getState().showNotification(`⚠️ Large project (${state.nodes.length} beats). May affect performance.`, 'warning', 5000);
    }

    const originalTitle = originalNode.data.title;
    const match = originalTitle.match(/^(.*?)\s*\((\d+)\)$/);

    let newTitle;
    if (match) {
      const base = match[1];
      const num = parseInt(match[2], 10);
      newTitle = `${base} (${num + 1})`;
    } else {
      newTitle = `${originalTitle} (2)`;
    }

    const duplicatedNode: BeatNode = {
      id: uuidv4(),
      type: 'beat',
      position,
      data: {
        ...originalNode.data,
        title: newTitle,
      },
    };

    if (originalNode.data.imagePath) {
      if (originalNode.data.imagePath.startsWith('node-image:')) {
        blobManager.addRef(originalNode.data.imagePath);
      } else {
        addImageRef(originalNode.data.imagePath);
      }
    }

    set((state) => ({
      nodes: [...state.nodes, duplicatedNode],
      isDirty: true,
    }));

    return duplicatedNode;
  },

  selectNode: (nodeId) => {
    set({ selectedNodeIds: nodeId ? [nodeId] : [] });
  },

  selectNodes: (nodeIds) => {
    set({ selectedNodeIds: nodeIds });
  },

  toggleNodeSelection: (nodeId) => {
    set((state) => ({
      selectedNodeIds: state.selectedNodeIds.includes(nodeId)
        ? state.selectedNodeIds.filter((id) => id !== nodeId)
        : [...state.selectedNodeIds, nodeId],
    }));
  },

  clearSelection: () => {
    set({ selectedNodeIds: [] });
  },

  copySelectedNodes: () => {
    const state = get();
    const selectedNodes = state.nodes.filter((node) =>
      state.selectedNodeIds.includes(node.id)
    );

    set({
      clipboard: selectedNodes.map(node => ({
        ...node,
        data: { ...node.data }
      }))
    });
  },

  pasteNodes: () => {
    const state = get();

    if (state.clipboard.length === 0) return;
    if (!state.lastClickedPosition) return;

    const currentNodeCount = state.nodes.length;
    const newNodeCount = currentNodeCount + state.clipboard.length;

    if (newNodeCount > NODE_MAX_COUNT) {
      const allowedCount = NODE_MAX_COUNT - currentNodeCount;
      if (allowedCount <= 0) {
        alert(`Maximum ${NODE_MAX_COUNT} beats reached. Cannot paste more nodes.`);
        return;
      }
      alert(`You can only paste ${allowedCount} more nodes (current: ${currentNodeCount}, max: ${NODE_MAX_COUNT}).`);
      return;
    }

    if (newNodeCount >= NODE_WARNING_COUNT && currentNodeCount < NODE_WARNING_COUNT) {
      useNotificationStore.getState().showNotification(`⚠️ Pasting will create ${newNodeCount} beats. May affect performance.`, 'warning', 5000);
    }

    state.saveHistory();

    const getIncrementedTitle = (title: string): string => {
      const match = title.match(/^(.*?)\s*\((\d+)\)$/);
      if (match) {
        const base = match[1];
        const num = parseInt(match[2], 10);
        return `${base} (${num + 1})`;
      }
      return `${title} (2)`;
    };

    const NODE_WIDTH = 180;
    const NODE_HEIGHT = 200;

    const firstNode = state.clipboard[0];
    const firstNodeCenterX = firstNode.position.x + NODE_WIDTH / 2;
    const firstNodeCenterY = firstNode.position.y + NODE_HEIGHT / 2;

    const offsetX = state.lastClickedPosition.x - firstNodeCenterX;
    const offsetY = state.lastClickedPosition.y - firstNodeCenterY;

    const newNodes: BeatNode[] = state.clipboard.map((node) => {
      if (node.data.imagePath) {
        if (node.data.imagePath.startsWith('node-image:')) {
          blobManager.addRef(node.data.imagePath);
        } else {
          addImageRef(node.data.imagePath);
        }
      }
      return {
        ...node,
        id: uuidv4(),
        position: {
          x: node.position.x + offsetX,
          y: node.position.y + offsetY,
        },
        data: {
          ...node.data,
          title: getIncrementedTitle(node.data.title)
        }
      };
    });

    set({
      nodes: [...state.nodes, ...newNodes],
      selectedNodeIds: newNodes.map(n => n.id),
      clipboard: [],
      isDirty: true,
    });
  },

  setLastClickedPosition: (position) => {
    set({ lastClickedPosition: position });
  },

  updateSelectedNodesColor: (color) => {
    const state = get();

    if (state.selectedNodeIds.length === 0) return;

    state.saveHistory();

    set((draft) => {
      for (const node of draft.nodes) {
        if (state.selectedNodeIds.includes(node.id)) {
          node.data.color = color;
        }
      }
      draft.isDirty = true;
    });
  },

  duplicateSelectedNodesInPlace: () => {
    const state = get();

    if (state.selectedNodeIds.length === 0) return [];

    const currentNodeCount = state.nodes.length;
    const newNodeCount = currentNodeCount + state.selectedNodeIds.length;

    if (newNodeCount > NODE_MAX_COUNT) {
      const allowedCount = NODE_MAX_COUNT - currentNodeCount;
      if (allowedCount <= 0) {
        alert(`Maximum ${NODE_MAX_COUNT} beats reached. Cannot duplicate.`);
        return [];
      }
      alert(`You can only duplicate ${allowedCount} more nodes (current: ${currentNodeCount}, max: ${NODE_MAX_COUNT}).`);
      return [];
    }

    if (newNodeCount >= NODE_WARNING_COUNT && currentNodeCount < NODE_WARNING_COUNT) {
      useNotificationStore.getState().showNotification(`⚠️ Duplicating will create ${newNodeCount} beats. May affect performance.`, 'warning', 5000);
    }

    state.saveHistory();

    const getIncrementedTitle = (title: string): string => {
      const match = title.match(/^(.*?)\s*\((\d+)\)$/);
      if (match) {
        const base = match[1];
        const num = parseInt(match[2], 10);
        return `${base} (${num + 1})`;
      }
      return `${title} (2)`;
    };

    const duplicateNodes: BeatNode[] = state.nodes
      .filter(node => state.selectedNodeIds.includes(node.id))
      .map((node) => {
        if (node.data.imagePath) {
          if (node.data.imagePath.startsWith('node-image:')) {
            blobManager.addRef(node.data.imagePath);
          } else {
            addImageRef(node.data.imagePath);
          }
        }
        return {
          ...node,
          id: uuidv4(),
          position: { ...node.position },
          data: { ...node.data }
        };
      });

    set((draft) => {
      for (const node of draft.nodes) {
        if (state.selectedNodeIds.includes(node.id)) {
          node.data.title = getIncrementedTitle(node.data.title);
        }
      }
      draft.nodes.push(...duplicateNodes);
      draft.isDirty = true;
    });

    return state.selectedNodeIds;
  },

  moveSelectedNodesToShelf: () => {
    const state = get();
    if (state.selectedNodeIds.length === 0) return;

    state.saveHistory();

    set((draft) => {
      for (const node of draft.nodes) {
        if (state.selectedNodeIds.includes(node.id)) {
          node.data.onShelf = true;
          node.data.shelfStoredPosition = { x: node.position.x, y: node.position.y };
        }
      }
      draft.isDirty = true;
    });
  },

  reorderNodeInTimeline: (nodeId: string, targetIndex: number) => {
    const state = get();

    state.saveHistory();

    const activeNodes = state.nodes.filter((n) => !n.data.onShelf);
    const sortedNodes = [...activeNodes].sort((a, b) => a.position.x - b.position.x);

    const draggedNodeIndex = sortedNodes.findIndex((n) => n.id === nodeId);
    if (draggedNodeIndex === -1) return;

    if (draggedNodeIndex === targetIndex) return;

    const draggedNode = sortedNodes[draggedNodeIndex];
    sortedNodes.splice(draggedNodeIndex, 1);
    sortedNodes.splice(targetIndex, 0, draggedNode);

    const NODE_WIDTH = 180;
    const MIN_GAP = 40;
    const MIN_SPACING = NODE_WIDTH + MIN_GAP;

    const updatedPositions: Array<{ id: string; x: number; y: number }> = [];

    for (let i = 0; i < sortedNodes.length; i++) {
      const node = sortedNodes[i];

      if (i === 0) {
        if (node.id === nodeId) {
          const secondNode = sortedNodes[1];
          if (secondNode) {
            const targetX = Math.max(0, secondNode.position.x - MIN_SPACING);
            updatedPositions.push({ id: node.id, x: targetX, y: node.position.y });
          } else {
            updatedPositions.push({ id: node.id, x: node.position.x, y: node.position.y });
          }
        } else {
          updatedPositions.push({ id: node.id, x: node.position.x, y: node.position.y });
        }
      } else {
        const prevUpdated = updatedPositions[i - 1];
        const expectedMinX = prevUpdated.x + MIN_SPACING;

        if (node.id === nodeId) {
          updatedPositions.push({ id: node.id, x: expectedMinX, y: node.position.y });
        } else if (node.position.x < expectedMinX) {
          updatedPositions.push({ id: node.id, x: expectedMinX, y: node.position.y });
        } else {
          updatedPositions.push({ id: node.id, x: node.position.x, y: node.position.y });
        }
      }
    }

    set((draft) => {
      for (const node of draft.nodes) {
        const updatedPos = updatedPositions.find((p) => p.id === node.id);
        if (updatedPos) {
          node.position = { x: updatedPos.x, y: updatedPos.y };
        }
      }
      draft.isDirty = true;
    });
  },

  // ---------------------------------------------------------------------------
  // OVERLAY ACTIONS
  // ---------------------------------------------------------------------------

  addOverlay: (nodeId: string, overlayType: CameraMovementType) => {
    const state = get();
    const node = state.nodes.find((n) => n.id === nodeId);
    if (!node) return;

    let imagePath: string | undefined;
    if (overlayType === 'push-in') {
      imagePath = 'arrow-inward.png';
    } else if (overlayType === 'pull-out') {
      imagePath = 'arrow-outward.png';
    } else if (overlayType === 'truck-left' || overlayType === 'truck-right') {
      imagePath = 'arrow_left.png';
    } else if (overlayType === 'pan-left' || overlayType === 'pan-right') {
      imagePath = 'arrow_curve_left.png';
    } else if (overlayType === 'tilt-up' || overlayType === 'tilt-down') {
      imagePath = 'arrow_curve_up.png';
    } else if (overlayType === 'boom-up' || overlayType === 'boom-down') {
      imagePath = 'arrow_up.png';
    }

    const newOverlay: CameraOverlay = {
      id: uuidv4(),
      type: overlayType,
      position: { x: 0.5, y: 0.5 },
      rotation: 0,
      scale: 1,
      opacity: 0.8,
      color: '#FFFFFF',
      imagePath,
      framePosition: (overlayType === 'zoom-in' || overlayType === 'zoom-out' || overlayType === 'frame')
        ? { x: 0.5, y: 0.5 }
        : undefined,
      frameSize: (overlayType === 'zoom-in' || overlayType === 'zoom-out' || overlayType === 'frame')
        ? 0.5
        : undefined,
    };

    const overlays = [...(node.data.overlays || []), newOverlay];
    state.updateNode(nodeId, { overlays });
    state.markDirty();
  },

  removeOverlay: (nodeId: string, overlayId: string) => {
    const state = get();
    const node = state.nodes.find((n) => n.id === nodeId);
    if (!node || !node.data.overlays) return;

    const overlays = node.data.overlays.filter((o) => o.id !== overlayId);
    state.updateNode(nodeId, { overlays });
    state.markDirty();
  },

  updateOverlay: (nodeId: string, overlayId: string, updates: Partial<CameraOverlay>) => {
    const state = get();
    const node = state.nodes.find((n) => n.id === nodeId);
    if (!node || !node.data.overlays) return;

    const overlays = node.data.overlays.map((o) =>
      o.id === overlayId ? { ...o, ...updates } : o
    );

    state.updateNode(nodeId, { overlays });
    state.markDirty();
  },

  // ---------------------------------------------------------------------------
  // TEXT OVERLAY ACTIONS
  // ---------------------------------------------------------------------------

  addTextOverlay: (nodeId: string, text: string = 'New Text') => {
    const state = get();
    const node = state.nodes.find((n) => n.id === nodeId);
    if (!node) return;

    const newTextOverlay: TextOverlay = {
      id: uuidv4(),
      text,
      position: { x: 0.5, y: 0.5 },
      fontSize: 24,
      color: '#FFFFFF',
      fontFamily: 'Arial',
      alignment: 'center',
      opacity: 1,
      fontWeight: 'normal',
      fontStyle: 'normal',
    };

    const textOverlays = [...(node.data.textOverlays || []), newTextOverlay];
    state.updateNode(nodeId, { textOverlays });
    state.markDirty();
  },

  removeTextOverlay: (nodeId: string, textOverlayId: string) => {
    const state = get();
    const node = state.nodes.find((n) => n.id === nodeId);
    if (!node || !node.data.textOverlays) return;

    const textOverlays = node.data.textOverlays.filter((o) => o.id !== textOverlayId);
    state.updateNode(nodeId, { textOverlays });
    state.markDirty();
  },

  updateTextOverlay: (nodeId: string, textOverlayId: string, updates: Partial<TextOverlay>) => {
    const state = get();
    const node = state.nodes.find((n) => n.id === nodeId);
    if (!node || !node.data.textOverlays) return;

    const textOverlays = node.data.textOverlays.map((o) =>
      o.id === textOverlayId ? { ...o, ...updates } : o
    );

    state.updateNode(nodeId, { textOverlays });
    state.markDirty();
  },

  // ---------------------------------------------------------------------------
  // TITLE NODE ACTIONS
  // ---------------------------------------------------------------------------

  addTitleNode: (startTime, duration, data = {}) => {
    const state = get();

    const newTitleNode: TitleNode = {
      id: uuidv4(),
      type: 'title',
      position: { x: startTime, y: 200 },
      data: {
        ...DEFAULT_TITLE_DATA,
        ...data,
        startTime,
        duration,
      } as TitleNodeData,
    };

    set({
      titleNodes: [...state.titleNodes, newTitleNode],
      isDirty: true,
    });

    return newTitleNode;
  },

  updateTitleNode: (nodeId, data) => {
    set((draft) => {
      const node = draft.titleNodes.find(n => n.id === nodeId);
      if (node) {
        Object.assign(node.data, data);
      }
      draft.isDirty = true;
    });
  },

  deleteTitleNode: (nodeId) => {
    const state = get();
    state.saveHistory();
    set({
      titleNodes: state.titleNodes.filter(n => n.id !== nodeId),
      isDirty: true,
    });
  },

  updateTitleNodePosition: (nodeId, startTime) => {
    set((draft) => {
      const node = draft.titleNodes.find(n => n.id === nodeId);
      if (node) {
        node.data.startTime = startTime;
        node.position.x = startTime;
      }
      draft.isDirty = true;
    });
  },

  updateTitleNodeDuration: (nodeId, duration) => {
    set((draft) => {
      const node = draft.titleNodes.find(n => n.id === nodeId);
      if (node) {
        node.data.duration = duration;
      }
      draft.isDirty = true;
    });
  },

  selectTitleNode: (nodeId) => {
    set((draft) => {
      draft.selectedTitleIds = nodeId ? [nodeId] : [];
    });
  },

  selectTitleNodes: (nodeIds) => {
    set((draft) => {
      draft.selectedTitleIds = nodeIds;
    });
  },

  clearTitleSelection: () => {
    set((draft) => {
      draft.selectedTitleIds = [];
    });
  },

  duplicateTitleNode: (nodeId, startTime) => {
    const state = get();
    const originalNode = state.titleNodes.find((n) => n.id === nodeId);

    if (!originalNode) return null;

    const newNode: TitleNode = {
      ...originalNode,
      id: uuidv4(),
      position: { ...originalNode.position },
      data: {
        ...originalNode.data,
        title: `${originalNode.data.title} Copy`,
        startTime: startTime ?? originalNode.data.startTime,
      },
    };

    set((draft) => {
      draft.titleNodes.push(newNode);
      draft.selectedTitleIds = [newNode.id];
      draft.isDirty = true;
    });

    return newNode;
  },

  // ---------------------------------------------------------------------------
  // AUDIO TRACK ACTIONS
  // ---------------------------------------------------------------------------

  addAudioTrack: (audioData, fileName, fileType, duration, startTime = 0) => {
    const newTrack: AudioTrack = {
      id: uuidv4(),
      ...DEFAULT_AUDIO_TRACK,
      name: fileName.replace(/\.[^/.]+$/, ''), // Remove extension
      audioData,
      fileName,
      fileType,
      duration,
      startTime,
    };

    set((draft) => {
      draft.audioTracks.push(newTrack);
      draft.isDirty = true;
    });

    // Extract waveform asynchronously after track is created
    console.log('[AudioTrack] Starting waveform extraction for:', newTrack.name);
    extractWaveform(audioData, 150)
      .then((waveformData) => {
        if (waveformData) {
          console.log('[AudioTrack] Waveform extracted, samples:', waveformData.samples.length);
          try {
            // Convert Uint8Array to base64 for storage
            const samplesBase64 = arrayBufferToBase64(waveformData.samples);
            
            set((draft) => {
              const track = draft.audioTracks.find((t) => t.id === newTrack.id);
              if (track) {
                track.waveform = {
                  samples: samplesBase64,
                  samplesPerSecond: waveformData.samplesPerSecond,
                  duration: waveformData.duration,
                };
                draft.isDirty = true;
                console.log('[AudioTrack] Waveform saved to track');
              }
            });
          } catch (err) {
            console.error('[AudioTrack] Failed to encode waveform:', err);
          }
        } else {
          console.warn('[AudioTrack] Waveform extraction returned null');
        }
      })
      .catch((err) => {
        console.error('[AudioTrack] Waveform extraction failed:', err);
      });

    return newTrack;
  },

  updateAudioTrack: (trackId, updates) => {
    set((draft) => {
      const track = draft.audioTracks.find((t) => t.id === trackId);
      if (track) {
        Object.assign(track, updates);
      }
      draft.isDirty = true;
    });
  },

  deleteAudioTrack: (trackId) => {
    set((draft) => {
      draft.audioTracks = draft.audioTracks.filter((t) => t.id !== trackId);
      draft.selectedAudioIds = draft.selectedAudioIds.filter((id) => id !== trackId);
      draft.isDirty = true;
    });
  },

  updateAudioTrackPosition: (trackId, startTime) => {
    set((draft) => {
      const track = draft.audioTracks.find((t) => t.id === trackId);
      if (track) {
        track.startTime = Math.max(0, startTime);
      }
      draft.isDirty = true;
    });
  },

  selectAudioTrack: (trackId) => {
    set((draft) => {
      draft.selectedAudioIds = trackId ? [trackId] : [];
    });
  },

  selectAudioTracks: (trackIds) => {
    set((draft) => {
      draft.selectedAudioIds = trackIds;
    });
  },

  clearAudioSelection: () => {
    set((draft) => {
      draft.selectedAudioIds = [];
    });
  },

  // ---------------------------------------------------------------------------
  // COMMENT BOX ACTIONS
  // ---------------------------------------------------------------------------

  createCommentBox: () => {
    const state = get();
    if (state.selectedNodeIds.length === 0) return null;

    const selectedNodes = state.nodes.filter(node => state.selectedNodeIds.includes(node.id));
    if (selectedNodes.length === 0) return null;

    const padding = 50;
    const minX = Math.min(...selectedNodes.map(n => n.position.x)) - padding;
    const minY = Math.min(...selectedNodes.map(n => n.position.y)) - padding;
    const maxX = Math.max(...selectedNodes.map(n => n.position.x + 300)) + padding;
    const maxY = Math.max(...selectedNodes.map(n => n.position.y + 200)) + padding;

    const newBox: CommentBoxNode = {
      id: uuidv4(),
      type: 'commentBox',
      position: { x: minX, y: minY },
      width: maxX - minX,
      height: maxY - minY,
      data: {
        title: 'Comment',
        color: '#E47019',
        nodeIds: state.selectedNodeIds,
      },
      zIndex: -1,
    };

    state.saveHistory();

    set({
      commentBoxes: [...state.commentBoxes, newBox],
      isDirty: true,
    });

    return newBox;
  },

  updateCommentBox: (boxId, updates) => {
    const state = get();

    if (updates.data) {
      state.saveHistory();
    }

    set((draft) => {
      const box = draft.commentBoxes.find(b => b.id === boxId);
      if (box) {
        if (updates.position) box.position = updates.position;
        if (updates.width !== undefined) box.width = updates.width;
        if (updates.height !== undefined) box.height = updates.height;
        if (updates.data) Object.assign(box.data, updates.data);
      }
      draft.isDirty = true;
    });
  },

  deleteCommentBox: (boxId) => {
    const state = get();

    state.saveHistory();

    set({
      commentBoxes: state.commentBoxes.filter(box => box.id !== boxId),
      isDirty: true,
    });
  },

  // ---------------------------------------------------------------------------
  // REFERENCE NODE ACTIONS
  // ---------------------------------------------------------------------------

  addReferenceNode: (position, data = {}) => {
    const state = get();

    let title = data.title || DEFAULT_REFERENCE_DATA.title;
    const existingTitles = state.referenceNodes.map(n => n.data.title);
    if (existingTitles.includes(title)) {
      let counter = 2;
      const baseName = title;
      while (existingTitles.includes(`${baseName} ${counter}`)) {
        counter++;
      }
      title = `${baseName} ${counter}`;
    }

    const newNode: ReferenceNode = {
      id: uuidv4(),
      type: 'reference',
      position,
      data: {
        ...DEFAULT_REFERENCE_DATA,
        ...data,
        title,
      },
    };

    set((state) => ({
      referenceNodes: [...state.referenceNodes, newNode],
      isDirty: true,
    }));

    return newNode;
  },

  updateReferenceNode: (nodeId, data) => {
    set((draft) => {
      const node = draft.referenceNodes.find((n) => n.id === nodeId);
      if (node) {
        Object.assign(node.data, data);
        draft.isDirty = true;
      }
    });
  },

  updateReferencePosition: (nodeId, position) => {
    set((draft) => {
      const node = draft.referenceNodes.find((n) => n.id === nodeId);
      if (node) {
        node.position = position;
        draft.isDirty = true;
      }
    });
  },

  deleteReferenceNode: (nodeId) => {
    const state = get();
    const node = state.referenceNodes.find((n) => n.id === nodeId);

    if (node?.data.imagePath) {
      const imagePath = node.data.imagePath;
      blobManager.revoke(imagePath);
    }

    state.saveHistory();

    set({
      referenceNodes: state.referenceNodes.filter(node => node.id !== nodeId),
      selectedReferenceIds: state.selectedReferenceIds.filter(id => id !== nodeId),
      isDirty: true,
    });
  },

  duplicateReferenceNode: (nodeId, position) => {
    const state = get();
    const originalNode = state.referenceNodes.find((n) => n.id === nodeId);

    if (!originalNode) return null;

    const newNode: ReferenceNode = {
      ...originalNode,
      id: uuidv4(),
      position,
      data: {
        ...originalNode.data,
        title: `${originalNode.data.title} Copy`,
      },
    };

    set((draft) => {
      draft.referenceNodes.push(newNode);
      draft.isDirty = true;
    });

    return newNode;
  },

  selectReferenceNode: (nodeId) => {
    set((draft) => {
      draft.selectedReferenceIds = nodeId ? [nodeId] : [];
    });
  },

  selectReferenceNodes: (nodeIds) => {
    set((draft) => {
      draft.selectedReferenceIds = nodeIds;
    });
  },

  clearReferenceSelection: () => {
    set((draft) => {
      draft.selectedReferenceIds = [];
    });
  },

  addTagToHistory: (tag) => {
    set((draft) => {
      const trimmedTag = tag.trim();
      if (!trimmedTag) return;

      if (!draft.tagHistory.includes(trimmedTag)) {
        draft.tagHistory.unshift(trimmedTag);
        if (draft.tagHistory.length > 50) {
          draft.tagHistory.pop();
        }
      } else {
        const index = draft.tagHistory.indexOf(trimmedTag);
        draft.tagHistory.splice(index, 1);
        draft.tagHistory.unshift(trimmedTag);
      }
      draft.isDirty = true;
    });
  },

  sendReferenceAsBeatToEnd: (referenceId) => {
    const state = get();
    const reference = state.referenceNodes.find(n => n.id === referenceId);

    if (!reference) return;

    state.saveHistory();

    const activeNodes = state.nodes.filter(n => !n.data.onShelf);
    const sortedNodes = [...activeNodes].sort((a, b) => a.position.x - b.position.x);
    const SPACING = 200;

    let targetPosition: { x: number; y: number };
    if (sortedNodes.length > 0) {
      const lastNode = sortedNodes[sortedNodes.length - 1];
      targetPosition = { x: lastNode.position.x + SPACING, y: lastNode.position.y };
    } else {
      targetPosition = { x: 0, y: 0 };
    }

    state.addNode(targetPosition, {
      title: reference.data.title,
      imagePath: reference.data.imagePath,
      script: reference.data.notes || '',
      color: reference.data.color,
      imageOffsetX: reference.data.imageOffsetX,
      imageOffsetY: reference.data.imageOffsetY,
      imageScale: reference.data.imageScale,
    });

    const { showNotification } = useNotificationStore.getState();
    showNotification('Reference added to end of timeline', 'info', 2000);

    const { autoSwitchViewAfterSend } = useViewStore.getState();
    if (autoSwitchViewAfterSend) {
      requestAnimationFrame(() => {
        useViewStore.getState().setActiveView('beats');
      });
    }
  },

  sendReferenceAsBeatAtPosition: (referenceId) => {
    const state = get();
    const reference = state.referenceNodes.find(n => n.id === referenceId);

    if (!reference) return;

    const focusedNodeId = usePlaybackStore.getState().focusedNodeId;
    const SPACING = 200;
    let targetPosition: { x: number; y: number };

    const activeNodes = state.nodes.filter(n => !n.data.onShelf);
    const sortedNodes = [...activeNodes].sort((a, b) => a.position.x - b.position.x);

    const targetNodeId = focusedNodeId || (state.selectedNodeIds.length > 0 ? state.selectedNodeIds[0] : null);

    if (targetNodeId) {
      const selectedNode = state.nodes.find(n => n.id === targetNodeId);

      if (selectedNode && !selectedNode.data.onShelf) {
        const selectedIndex = sortedNodes.findIndex(n => n.id === selectedNode.id);

        if (selectedIndex !== -1) {
          targetPosition = {
            x: selectedNode.position.x + SPACING,
            y: selectedNode.position.y,
          };

          for (let i = selectedIndex + 1; i < sortedNodes.length; i++) {
            const currentNode = sortedNodes[i];
            const expectedMinX = targetPosition.x + (i - selectedIndex) * SPACING;

            if (currentNode.position.x < expectedMinX) {
              state.updateNodePosition(currentNode.id, {
                x: expectedMinX,
                y: currentNode.position.y,
              });
            }
          }
        } else {
          if (sortedNodes.length > 0) {
            const lastNode = sortedNodes[sortedNodes.length - 1];
            targetPosition = { x: lastNode.position.x + SPACING, y: lastNode.position.y };
          } else {
            targetPosition = { x: 0, y: 0 };
          }
        }
      } else {
        if (sortedNodes.length > 0) {
          const lastNode = sortedNodes[sortedNodes.length - 1];
          targetPosition = { x: lastNode.position.x + SPACING, y: lastNode.position.y };
        } else {
          targetPosition = { x: 0, y: 0 };
        }
      }
    } else {
      if (sortedNodes.length > 0) {
        const lastNode = sortedNodes[sortedNodes.length - 1];
        targetPosition = { x: lastNode.position.x + SPACING, y: lastNode.position.y };
      } else {
        targetPosition = { x: 0, y: 0 };
      }
    }

    state.saveHistory();

    state.addNode(targetPosition, {
      title: reference.data.title,
      imagePath: reference.data.imagePath,
      script: reference.data.notes || '',
      color: reference.data.color,
      imageOffsetX: reference.data.imageOffsetX,
      imageOffsetY: reference.data.imageOffsetY,
      imageScale: reference.data.imageScale,
    });

    const { showNotification } = useNotificationStore.getState();
    showNotification('Reference added after selected beat', 'info', 2000);

    const { autoSwitchViewAfterSend } = useViewStore.getState();
    if (autoSwitchViewAfterSend) {
      requestAnimationFrame(() => {
        useViewStore.getState().setActiveView('beats');
      });
    }
  },

  // ---------------------------------------------------------------------------
  // NOTE NODE ACTIONS
  // ---------------------------------------------------------------------------

  addNoteNode: (position, data = {}) => {
    const state = get();

    let title = data.title || DEFAULT_NOTE_DATA.title;
    const existingTitles = state.noteNodes.map(n => n.data.title);
    if (existingTitles.includes(title)) {
      let counter = 2;
      const baseName = title;
      while (existingTitles.includes(`${baseName} ${counter}`)) {
        counter++;
      }
      title = `${baseName} ${counter}`;
    }

    const newNode: NoteNode = {
      id: uuidv4(),
      type: 'note',
      position,
      width: 300,
      height: 200,
      data: {
        ...DEFAULT_NOTE_DATA,
        ...data,
        title,
      },
    };

    set((state) => ({
      noteNodes: [...state.noteNodes, newNode],
      isDirty: true,
    }));

    return newNode;
  },

  updateNoteNode: (nodeId, data) => {
    set((draft) => {
      const node = draft.noteNodes.find((n) => n.id === nodeId);
      if (node) {
        Object.assign(node.data, data);
        draft.isDirty = true;
      }
    });
  },

  updateNotePosition: (nodeId, position) => {
    set((draft) => {
      const node = draft.noteNodes.find((n) => n.id === nodeId);
      if (node) {
        node.position = position;
        draft.isDirty = true;
      }
    });
  },

  updateNoteSize: (nodeId, width, height) => {
    set((draft) => {
      const node = draft.noteNodes.find((n) => n.id === nodeId);
      if (node) {
        node.width = width;
        node.height = height;
        draft.isDirty = true;
      }
    });
  },

  deleteNoteNode: (nodeId) => {
    const state = get();

    state.saveHistory();

    set({
      noteNodes: state.noteNodes.filter(node => node.id !== nodeId),
      selectedNoteIds: state.selectedNoteIds.filter(id => id !== nodeId),
      isDirty: true,
    });
  },

  duplicateNoteNode: (nodeId, position) => {
    const state = get();
    const originalNode = state.noteNodes.find((n) => n.id === nodeId);

    if (!originalNode) return null;

    const newNode: NoteNode = {
      ...originalNode,
      id: uuidv4(),
      position,
      data: {
        ...originalNode.data,
        title: `${originalNode.data.title} Copy`,
      },
    };

    set((draft) => {
      draft.noteNodes.push(newNode);
      draft.isDirty = true;
    });

    return newNode;
  },

  selectNoteNode: (nodeId) => {
    set((draft) => {
      draft.selectedNoteIds = nodeId ? [nodeId] : [];
    });
  },

  selectNoteNodes: (nodeIds) => {
    set((draft) => {
      draft.selectedNoteIds = nodeIds;
    });
  },

  clearNoteSelection: () => {
    set((draft) => {
      draft.selectedNoteIds = [];
    });
  },

  // ---------------------------------------------------------------------------
  // TUTORIAL ACTIONS
  // ---------------------------------------------------------------------------

  setShowTutorial: (show: boolean) => {
    set({ showTutorial: show });
    // Only persist to localStorage when explicitly dismissing (show = false)
    // Don't persist when just closing the tutorial
    if (typeof window !== 'undefined' && !show) {
      localStorage.setItem('emberboard-showTutorial', 'false');
    }
  },

  // Close tutorial without persisting to localStorage
  closeTutorial: () => {
    set({ showTutorial: false });
  },

  // Set tutorial preference in localStorage without closing
  setTutorialPreference: (dismiss: boolean) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('emberboard-showTutorial', dismiss ? 'false' : 'true');
    }
  },

  markDirty: () => {
    set({ isDirty: true });
  },

  // ---------------------------------------------------------------------------
  // PROJECT ACTIONS
  // ---------------------------------------------------------------------------

  newProject: (name = 'Untitled Project') => {
    const firstBeat: BeatNode = {
      id: uuidv4(),
      type: 'beat',
      position: { x: 0, y: 0 },
      data: {
        title: 'First Beat',
        sceneHeading: '',
        script: '',
        imagePath: null,
        duration: 3,
        onShelf: false,
      },
    };

    const shouldShowTutorial = typeof window !== 'undefined'
      ? localStorage.getItem('emberboard-showTutorial') !== 'false'
      : true;

    set({
      nodes: [firstBeat],
      commentBoxes: [],
      titleNodes: [],
      audioTracks: [],
      selectedNodeIds: [],
      selectedTitleIds: [],
      selectedAudioIds: [],
      projectPath: null,
      projectName: name,
      fps: DEFAULT_PROJECT.fps,
      aspectRatio: DEFAULT_PROJECT.aspectRatio,
      beatColorScheme: 'orange-grey',
      projectAuthor: DEFAULT_PROJECT.author || '',
      projectDescription: DEFAULT_PROJECT.description || '',
      isDirty: false,
      showTutorial: shouldShowTutorial,
    });
  },

  loadProject: (project, filePath) => {
    console.log('[ProjectStore] loadProject called with:', {
      nodesCount: project.nodes?.length || 0,
      referenceNodesCount: project.referenceNodes?.length || 0,
      nodeIds: project.nodes?.map((n: BeatNode) => n.id) || []
    });

    if (project.nodes && project.nodes.length > 0) {
      console.log('[ProjectStore] Node details:');
      project.nodes.forEach((node: BeatNode, index: number) => {
        console.log(`  Node ${index}: ID=${node.id}, title="${node.data.title}", pos=(${node.position.x}, ${node.position.y})`);
      });
    }

    imageRefCounts.clear();
    if (project.nodes && project.nodes.length > 0) {
      for (const node of project.nodes) {
        const imagePath = node.data.imagePath;
        if (imagePath && !imagePath.startsWith('node-image:')) {
          const count = imageRefCounts.get(imagePath) || 0;
          imageRefCounts.set(imagePath, count + 1);
        }
      }
      console.log('[ProjectStore] Initialized imageRefCounts:', Array.from(imageRefCounts.entries()));
    }

    const beatColorScheme = (project as any).beatColorScheme || 'orange-grey';
    applyBeatColorScheme(beatColorScheme);
    set({
      nodes: project.nodes,
      commentBoxes: project.commentBoxes || [],
      referenceNodes: project.referenceNodes || [],
      noteNodes: project.noteNodes || [],
      titleNodes: project.titleNodes || [],
      audioTracks: project.audioTracks || [],
      selectedNodeIds: [],
      selectedReferenceIds: [],
      selectedNoteIds: [],
      selectedTitleIds: [],
      selectedAudioIds: [],
      projectPath: filePath,
      projectName: project.name,
      fps: project.fps,
      aspectRatio: project.aspectRatio || '16:9',
      beatColorScheme,
      projectAuthor: project.author || '',
      projectDescription: project.description || '',
      sceneHeadingHistory: project.sceneHeadingHistory || [],
      tagHistory: project.tagHistory || [],
      isDirty: false,
    });

    console.log('[ProjectStore] loadProject completed, state now has:', {
      nodesCount: get().nodes.length,
      referenceNodesCount: get().referenceNodes.length
    });
  },

  getProjectData: () => {
    const state = get();
    const now = new Date().toISOString();

    return {
      version: '1.0',
      name: state.projectName,
      fps: state.fps,
      aspectRatio: state.aspectRatio,
      beatColorScheme: state.beatColorScheme,
      nodes: state.nodes,
      referenceNodes: state.referenceNodes,
      commentBoxes: state.commentBoxes,
      titleNodes: state.titleNodes,
      audioTracks: state.audioTracks,
      noteNodes: state.noteNodes,
      sceneHeadingHistory: state.sceneHeadingHistory,
      tagHistory: state.tagHistory,
      author: state.projectAuthor,
      description: state.projectDescription,
      createdAt: now,
      modifiedAt: now,
    } as any;
  },

  setProjectPath: (path) => {
    set({ projectPath: path });
  },

  setProjectName: (name) => {
    set({ projectName: name, isDirty: true });
  },

  setFps: (fps) => {
    set({ fps, isDirty: true });
  },

  setAspectRatio: (aspectRatio) => {
    set({ aspectRatio, isDirty: true });
  },

  setBeatColorScheme: (scheme) => {
    applyBeatColorScheme(scheme);
    set({ beatColorScheme: scheme, isDirty: true });
  },

  setProjectAuthor: (author) => {
    set({ projectAuthor: author, isDirty: true });
  },

  setProjectDescription: (description) => {
    set({ projectDescription: description, isDirty: true });
  },

  markSaved: () => {
    set({ isDirty: false });
  },

  // ---------------------------------------------------------------------------
  // UTILITY ACTIONS
  // ---------------------------------------------------------------------------

  getSortedNodes: () => {
    const { nodes } = get();
    return [...nodes].sort((a, b) => a.position.x - b.position.x);
  },

  getTotalDuration: () => {
    const { nodes } = get();
    return nodes
      .filter((node) => !node.data.onShelf)
      .reduce((total, node) => total + node.data.duration, 0);
  },

  // ---------------------------------------------------------------------------
  // HISTORY ACTIONS
  // ---------------------------------------------------------------------------

  saveHistory: () => {
    const { nodes, titleNodes } = get();

    const snapshot: StateSnapshot = {
      nodes: nodes.map((node) => ({
        id: node.id,
        position: { ...node.position },
        data: { ...node.data },
      })),
      titleNodes: JSON.parse(JSON.stringify(titleNodes)), // Deep clone
      timestamp: Date.now(),
    };

    useHistoryStore.getState().pushState(snapshot);
  },

  undo: () => {
    const historyStore = useHistoryStore.getState();
    const currentNodes = get().nodes;
    const currentTitleNodes = get().titleNodes;

    if (!historyStore.canUndo()) return;

    const currentSnapshot: StateSnapshot = {
      nodes: currentNodes.map((node) => ({
        id: node.id,
        position: { ...node.position },
        data: { ...node.data },
      })),
      titleNodes: JSON.parse(JSON.stringify(currentTitleNodes)),
      timestamp: Date.now(),
    };
    historyStore.addToFuture(currentSnapshot);

    const previousState = historyStore.undo();
    if (!previousState) return;

    const currentNodeIds = new Set(currentNodes.map(n => n.id));
    for (const nodeSnapshot of previousState.nodes) {
      if (!currentNodeIds.has(nodeSnapshot.id) && nodeSnapshot.data.imagePath) {
        historyStore.removePendingImageCleanup(nodeSnapshot.data.imagePath);
        console.log('[ProjectStore] Undo: Restored node image from pending cleanup:', nodeSnapshot.data.imagePath);
      }
    }

    get().restoreSnapshot(previousState);
  },

  redo: () => {
    const historyStore = useHistoryStore.getState();

    if (!historyStore.canRedo()) return;

    const currentSnapshot: StateSnapshot = {
      nodes: get().nodes.map((node) => ({
        id: node.id,
        position: { ...node.position },
        data: { ...node.data },
      })),
      titleNodes: JSON.parse(JSON.stringify(get().titleNodes)),
      timestamp: Date.now(),
    };
    historyStore.addToPast(currentSnapshot);

    const nextState = historyStore.redo();
    if (!nextState) return;

    get().restoreSnapshot(nextState);
  },

  restoreSnapshot: (snapshot) => {
    const historyStore = useHistoryStore.getState();

    historyStore.setApplyingHistory(true);

    const restoredNodes: BeatNode[] = snapshot.nodes.map((nodeSnapshot) => ({
      id: nodeSnapshot.id,
      type: 'beat',
      position: { ...nodeSnapshot.position },
      data: { ...nodeSnapshot.data },
    }));

    set({
      nodes: restoredNodes,
      titleNodes: snapshot.titleNodes ? JSON.parse(JSON.stringify(snapshot.titleNodes)) : [],
      isDirty: true,
    });

    historyStore.setApplyingHistory(false);
  },
})));

// =============================================================================
// SELECTOR HOOKS
// =============================================================================

/** Get the currently selected node */
export const useSelectedNode = () =>
  useProjectStore((state) => {
    const firstSelectedId = state.selectedNodeIds[0];
    if (!firstSelectedId) return null;
    return state.nodes.find((n) => n.id === firstSelectedId) || null;
  });

/** Get just the nodes array */
export const useNodes = () => useProjectStore((state) => state.nodes);

/** Get dirty state */
export const useIsDirty = () => useProjectStore((state) => state.isDirty);